import { useEffect, useRef, useState } from 'react';
import { debounce } from 'ts-debounce';

import type { HotelOffer, RoomWithPeople } from '@/interfaces';
import { HotelOfferCard } from './components/hotelOfferCard/HotelOfferCard';
import { Image } from '@/components/image/Image';
import classNames from 'classnames';
import { LoadingItem } from '@/components/loadingItem/LoadingItem';

interface Props {
    offers: readonly HotelOffer[];
    rooms: readonly RoomWithPeople[];
    title?: string;
    nights?: number;
    isDirect?: boolean;
    isPending?: boolean;
}

const maxScroll = (element?: HTMLElement): number => element?.scrollWidth - element?.clientWidth;
const currentScroll = (element?: HTMLElement): number => element?.scrollLeft;
const canScrollLeft = (element?: HTMLElement): boolean => currentScroll(element) > 0;
const canScrollRight = (element?: HTMLElement): boolean => currentScroll(element) < maxScroll(element);

export const HotelOffersCarousel = ({ offers, rooms, title, nights, isDirect, isPending = false }: Props) => {
    const [showLeftScroller, setShowLeftScroller] = useState(true);
    const [showRightScroller, setShowRightScroller] = useState(true);
    const scrollContainer = useRef(null);

    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
    const updateScrollButtons = (_1?: any, _2?: any) => {
        setShowLeftScroller(canScrollLeft(scrollContainer.current));
        setShowRightScroller(canScrollRight(scrollContainer.current));
    };
    const debouncedUpdateScrollButtons = debounce(updateScrollButtons, 1);

    useEffect(() => {
        updateScrollButtons();
        window?.addEventListener('resize', debouncedUpdateScrollButtons);

        return () => window?.removeEventListener('resize', debouncedUpdateScrollButtons);
    }, [offers, scrollContainer]);

    const doScrollLeft = () => scrollContainer.current?.scrollBy({ left: Math.min(window?.innerWidth, 1025) * -0.75, behavior: 'smooth' });
    const doScrollRight = () => scrollContainer.current?.scrollBy({ left: Math.min(window?.innerWidth, 1025) * 0.75, behavior: 'smooth' });

    const provider = offers.length > 0 ? offers[0].provider : undefined;

    return isPending || !provider ? (
        <div className="u-mt32 loading">
            <div className="image-container image-container-1x3 logo carousel-logo skeleton-screen" />
            <div className="u-t5 u-strong u-no-break skeleton-screen" />
            <div className="u-position-relative">
                <div className="u-mb64 u-display-flex u-position-relative carousel-scroll">
                    <div className="ads__unit compact u-float-left u-display-inline-block u-fit-height u-ma8 u-pa16">
                        <div className="skeleton-screen" />
                        <div className="u-position-absolute u-pt4 price skeleton-screen" />
                        <div className="button button--cta u-position-absolute button--order-hotel skeleton-screen" />
                    </div>
                    <div className="u-position-absolute u-left u-right">
                        <LoadingItem classNames="u-position-relative u-mt64 u-size1of1 loading" />
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <div className="u-mt32">
            <div className="image-container image-container-1x3 logo carousel-logo">
                <Image image={provider.logo} altText={provider.name} frame={false} />
            </div>
            <h4 className="u-t5 u-strong u-no-break u-overflow-hidden">
                {title ?? provider.name}
                <span className="u-d1 u-ml8" data-testid="offerCount">
                    {offers.length} tilbud
                </span>
            </h4>
            <div className="u-position-relative">
                <div
                    className={classNames('carousel left u-position-absolute u-hide-lt768', {
                        'u-display-block': showLeftScroller,
                        'u-display-none': !showLeftScroller,
                    })}
                    onClick={doScrollLeft}>
                    <div className="button button--pill button--has-icon icon icon--chevron-left u-position-absolute u-left" />
                </div>
                <div
                    ref={scrollContainer}
                    className="u-mb64 u-display-flex u-position-relative carousel-scroll"
                    onScroll={debouncedUpdateScrollButtons}>
                    {offers.map((offer) => (
                        <HotelOfferCard key={`carousel-${offer.id}`} offer={offer} rooms={rooms} nights={nights} isDirect={isDirect} />
                    ))}
                </div>
                <div
                    className={classNames('carousel right u-position-absolute u-hide-lt768', {
                        'u-display-block': showRightScroller,
                        'u-display-none': !showRightScroller,
                    })}
                    onClick={doScrollRight}>
                    <div className="button button--pill button--has-icon icon icon--chevron-right u-position-absolute u-right" />
                </div>
            </div>
        </div>
    );
};
